import React, { useEffect, useRef, useState } from 'react'
import { humanize } from 'inflection'
import { Text, XYChart, Box, Flex } from '@sequencehq/core-components'
import {
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey90,
  Link,
  Primary13SemiBold,
  Primary13Regular,
  Primary12Regular,
  ShadowXs,
  IndigoIndigo50,
  GreenGreen50,
  RedRed50,
  YellowYellow50,
  PurplePurple50,
  TurquoiseTurquoise50
} from '@sequencehq/core-components'

export type ChartComponentData = {
  type: 'chart'
  title: string
  data: Array<{ date: string; value: number }>
  unit: string
  variant: 'line' | 'bar' | 'area'
  dateFrom: string
  dateTo: string
  unitTickValues: number[]
}

export type PieChartComponentData = {
  type: 'pie'
  title: string
  data: Array<{ label: string; value: number; color?: string }>
  unit: string
  showPercentages?: boolean
}

export type BarChartComponentData = {
  type: 'horizontal-bar'
  title: string
  data: Array<{ label: string; value: number }>
  unit: string
  maxValue?: number
}

export type StackedBarComponentData = {
  type: 'stacked-bar'
  title: string
  data: Array<{
    label: string
    values: Array<{ name: string; value: number; color?: string }>
  }>
  unit: string
  categories: string[]
}

export type TableComponentData = {
  type: 'table'
  title: string
  headers: string[]
  rows: Array<string[]>
  highlightColumn?: number
}

export type MetricComponentData = {
  type: 'metric'
  title: string
  value: string
  subtitle?: string
  trend?: {
    direction: 'up' | 'down' | 'neutral'
    percentage: string
  }
}

export type KPIDashboardComponentData = {
  type: 'kpi-dashboard'
  title: string
  metrics: Array<{
    label: string
    value: string
    subtitle?: string
    trend?: { direction: 'up' | 'down' | 'neutral'; percentage: string }
  }>
}

export type ComparisonComponentData = {
  type: 'comparison'
  title: string
  current: { label: string; value: string }
  previous: { label: string; value: string }
  change: {
    direction: 'up' | 'down' | 'neutral'
    percentage: string
    value: string
  }
}

export type ProgressComponentData = {
  type: 'progress'
  title: string
  current: number
  target: number
  unit: string
  subtitle?: string
}

export type InsightComponentData = {
  type: 'insight'
  title: string
  message: string
  severity: 'info' | 'warning' | 'success' | 'error'
  icon?: string
}

export type GenerativeComponentData =
  | ChartComponentData
  | PieChartComponentData
  | BarChartComponentData
  | StackedBarComponentData
  | TableComponentData
  | MetricComponentData
  | KPIDashboardComponentData
  | ComparisonComponentData
  | ProgressComponentData
  | InsightComponentData
  | InvoiceCardComponentData
  | ScheduleCardComponentData
  | ProductCardComponentData
  | CustomerPillComponentData

export type InvoiceCardComponentData = {
  type: 'invoice-card'
  title: string
  invoiceId: string
  invoiceNumber: string
  amount: string
  status?: string
  url?: string
}

export type ScheduleCardComponentData = {
  type: 'schedule-card'
  title: string
  scheduleId: string
  customerId?: string
  status?: string
  mrr?: string
}

// Simple Chart Component
export const SimpleChart: React.FC<ChartComponentData> = ({
  title,
  data,
  unit,
  variant,
  dateFrom,
  dateTo,
  unitTickValues
}) => {
  // Measure container to avoid XYChart mounting with zero width/height
  const containerRef = useRef<HTMLDivElement>(null)
  const [canRender, setCanRender] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const rect = el.getBoundingClientRect()
      setCanRender(rect.width > 0 && rect.height > 0)
    }
    update()
    let ro: any = undefined
    const RO: any = (window as any).ResizeObserver
    if (typeof RO === 'function') {
      ro = new RO(() => update())
      if (el) ro.observe(el)
    }
    const onResize = () => update()
    window.addEventListener('resize', onResize)
    return () => {
      if (ro && el) ro.unobserve(el)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const currencyFormat = (value: number) => {
    return `${unit}${Intl.NumberFormat('en-GB', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      notation: value >= 1000 ? 'compact' : 'standard'
    }).format(value)}`
  }

  const validData = Array.isArray(data) ? data : []
  const validUnitTickValues =
    Array.isArray(unitTickValues) && unitTickValues.length > 0
      ? unitTickValues
      : [0, Math.max(...validData.map(d => d.value || 0), 1000)]

  if (validData.length === 0) {
    return (
      <Box
        bg="white"
        borderRadius="8px"
        border={`1px solid ${GreyGrey30}`}
        boxShadow={ShadowXs}
        p={4}
        mb={3}
      >
        <Text {...Primary13SemiBold} color={GreyGrey90} mb={3}>
          {title}
        </Text>
        <Box
          height="200px"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="#f8f9fa"
          borderRadius="6px"
        >
          <Text {...Primary13Regular} color={GreyGrey60}>
            No data available for chart
          </Text>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="800px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={3}>
        {title}
      </Text>
      <Box ref={containerRef} height="300px" width="100%">
        {canRender ? (
          <XYChart
            variant={variant === 'area' ? 'line' : variant}
            dateFrom={dateFrom}
            dateTo={dateTo}
            data={validData}
            unitTickValues={validUnitTickValues}
            unit={unit}
            renderTooltip={({ date, value }) => (
              <Flex
                flexDir="column"
                bg="white"
                p={2}
                borderRadius="4px"
                boxShadow={ShadowXs}
              >
                <Text {...Primary13SemiBold} color={GreyGrey90}>
                  {new Date(date).toLocaleDateString()}
                </Text>
                <Text
                  as={Flex}
                  {...Primary13Regular}
                  color={GreyGrey60}
                  alignItems="center"
                  gap={2}
                >
                  <Flex h={2} w={2} bg={IndigoIndigo50} borderRadius="full" />
                  {currencyFormat(value)}
                </Text>
              </Flex>
            )}
          />
        ) : (
          <Box height="100%" width="100%" />
        )}
      </Box>
    </Box>
  )
}

export const SimpleTable: React.FC<TableComponentData> = ({
  title,
  headers,
  rows,
  highlightColumn
}) => {
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      overflow="hidden"
      mb={3}
      width="100%"
      maxWidth="800px"
    >
      <Box p={4} borderBottom={`1px solid ${GreyGrey30}`}>
        <Text {...Primary13SemiBold} color={GreyGrey90}>
          {title}
        </Text>
      </Box>
      <Box overflow="auto" maxHeight="300px">
        {/* Headers */}
        <Flex
          bg="#f8f9fa"
          borderBottom={`1px solid ${GreyGrey30}`}
          position="sticky"
          top={0}
          zIndex={1}
        >
          {headers.map((header, index) => (
            <Box
              key={index}
              p={3}
              flex={1}
              minWidth="100px"
              borderRight={
                index < headers.length - 1 ? `1px solid ${GreyGrey30}` : 'none'
              }
              bg={highlightColumn === index ? '#f0f4ff' : '#f8f9fa'}
            >
              <Text {...Primary12Regular} color={GreyGrey60} fontWeight="600">
                {header}
              </Text>
            </Box>
          ))}
        </Flex>

        {/* Rows */}
        {rows.map((row, rowIndex) => (
          <Flex
            key={rowIndex}
            borderBottom={
              rowIndex < rows.length - 1 ? `1px solid ${GreyGrey30}` : 'none'
            }
            _hover={{ bg: '#f8f9fa' }}
          >
            {row.map((cell, cellIndex) => (
              <Box
                key={cellIndex}
                p={3}
                flex={1}
                minWidth="100px"
                borderRight={
                  cellIndex < row.length - 1
                    ? `1px solid ${GreyGrey30}`
                    : 'none'
                }
                bg={highlightColumn === cellIndex ? '#f9fafe' : 'transparent'}
              >
                <Text {...Primary13Regular} color={GreyGrey90}>
                  {cell}
                </Text>
              </Box>
            ))}
          </Flex>
        ))}
      </Box>
    </Box>
  )
}

export const MetricCard: React.FC<MetricComponentData> = ({
  title,
  value,
  subtitle,
  trend
}) => {
  const getTrendColor = () => {
    if (!trend) return GreyGrey60
    switch (trend.direction) {
      case 'up':
        return '#10b981' // green
      case 'down':
        return '#ef4444' // red
      default:
        return GreyGrey60
    }
  }

  const getTrendIcon = () => {
    if (!trend) return null
    switch (trend.direction) {
      case 'up':
        return '↗'
      case 'down':
        return '↘'
      default:
        return '→'
    }
  }

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="400px"
    >
      <Text {...Primary12Regular} color={GreyGrey60} mb={1}>
        {title}
      </Text>
      <Text {...Primary13SemiBold} fontSize="24px" color={GreyGrey90} mb={1}>
        {value}
      </Text>
      {subtitle && (
        <Text {...Primary12Regular} color={GreyGrey60} mb={2}>
          {subtitle}
        </Text>
      )}
      {trend && (
        <Flex align="center" gap={1}>
          <Text {...Primary12Regular} color={getTrendColor()} fontWeight="600">
            {getTrendIcon()} {trend.percentage}
          </Text>
        </Flex>
      )}
    </Box>
  )
}

export const PieChart: React.FC<PieChartComponentData> = ({
  title,
  data,
  unit,
  showPercentages = true
}) => {
  const [hoveredSlice, setHoveredSlice] = React.useState<number | null>(null)
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 })

  const total = data.reduce((sum, item) => sum + item.value, 0)
  const colors = [
    IndigoIndigo50,
    RedRed50,
    GreenGreen50,
    YellowYellow50,
    PurplePurple50,
    TurquoiseTurquoise50
  ]

  // Calculate angles for each slice
  const slices = data.map((item, index) => {
    const percentage = item.value / total
    const angle = percentage * 360
    return {
      ...item,
      percentage,
      angle,
      color: item.color || colors[index % colors.length]
    }
  })

  const handleMouseMove = (event: React.MouseEvent, index: number) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    })
    setHoveredSlice(index)
  }

  const handleMouseLeave = () => {
    setHoveredSlice(null)
  }

  // Create SVG path for pie slices
  const createPieSlice = (
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number
  ) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle)
    const end = polarToCartesian(centerX, centerY, radius, startAngle)
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

    return [
      'M',
      centerX,
      centerY,
      'L',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      'Z'
    ].join(' ')
  }

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    }
  }

  const centerX = 100
  const centerY = 100
  const radius = 80

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="600px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={3}>
        {title}
      </Text>
      <Flex direction="row" gap={4} align="center">
        <Box
          width="300px"
          height="200px"
          position="relative"
          display="flex"
          justifyContent="center"
        >
          <svg width="200" height="200" viewBox="0 0 200 200">
            {slices.map((slice, index) => {
              let currentAngle = 0
              // Calculate start angle for this slice
              for (let i = 0; i < index; i++) {
                currentAngle += slices[i].angle
              }
              const startAngle = currentAngle
              const endAngle = currentAngle + slice.angle
              const isHovered = hoveredSlice === index

              return (
                <path
                  key={slice.label}
                  d={createPieSlice(
                    centerX,
                    centerY,
                    radius,
                    startAngle,
                    endAngle
                  )}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth="2"
                  style={{
                    cursor: 'pointer',
                    opacity: isHovered ? 0.8 : 1,
                    filter: isHovered ? 'brightness(1.1)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseMove={e => handleMouseMove(e, index)}
                  onMouseLeave={handleMouseLeave}
                />
              )
            })}
            {/* Center circle for total */}
            <circle
              cx={centerX}
              cy={centerY}
              r="45"
              fill="white"
              stroke={GreyGrey30}
              strokeWidth="2"
            />
            <text
              x={centerX}
              y={centerY - 8}
              textAnchor="middle"
              fontSize="12"
              fontWeight="bold"
              fill={GreyGrey90}
            >
              Total
            </text>
            <text
              x={centerX}
              y={centerY + 8}
              textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill={GreyGrey90}
            >
              {unit}
              {total.toLocaleString()}
            </text>
          </svg>

          {/* Tooltip */}
          {hoveredSlice !== null && (
            <Box
              position="fixed"
              left={`${tooltipPosition.x + 10}px`}
              top={`${tooltipPosition.y - 10}px`}
              bg="rgba(0, 0, 0, 0.9)"
              color="white"
              p={2}
              borderRadius="6px"
              fontSize="12px"
              fontWeight="500"
              pointerEvents="none"
              zIndex={1000}
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              minWidth="120px"
            >
              <Text fontWeight="bold" mb={1}>
                {slices[hoveredSlice].label}
              </Text>
              <Text>
                {unit}
                {slices[hoveredSlice].value.toLocaleString()}
              </Text>
              <Text color="rgba(255, 255, 255, 0.8)">
                {(slices[hoveredSlice].percentage * 100).toFixed(1)}% of total
              </Text>
            </Box>
          )}
        </Box>
        <Box flex={1}>
          {data.map((item, index) => {
            const percentage = ((item.value / total) * 100).toFixed(1)
            return (
              <Flex key={item.label} align="center" gap={3} mb={2}>
                <Box
                  width="12px"
                  height="12px"
                  borderRadius="2px"
                  bg={item.color || colors[index % colors.length]}
                />
                <Text {...Primary13Regular} flex={1}>
                  {item.label}
                </Text>
                <Text {...Primary13SemiBold}>
                  {unit}
                  {item.value.toLocaleString()}
                  {showPercentages && (
                    <Text
                      as="span"
                      {...Primary12Regular}
                      color={GreyGrey60}
                      ml={1}
                    >
                      ({percentage}%)
                    </Text>
                  )}
                </Text>
              </Flex>
            )
          })}
        </Box>
      </Flex>
    </Box>
  )
}

export const HorizontalBarChart: React.FC<BarChartComponentData> = ({
  title,
  data,
  unit,
  maxValue
}) => {
  const max = maxValue || Math.max(...data.map(item => item.value))

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="800px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={3}>
        {title}
      </Text>
      <Box>
        {data.map((item, index) => (
          <Flex key={item.label} align="center" gap={3} mb={3}>
            <Text {...Primary13Regular} minWidth="120px" textAlign="right">
              {item.label}
            </Text>
            <Box
              flex={1}
              height="24px"
              bg="#f3f4f6"
              borderRadius="4px"
              position="relative"
            >
              <Box
                height="100%"
                bg={IndigoIndigo50}
                borderRadius="4px"
                width={`${(item.value / max) * 100}%`}
                transition="width 0.5s ease"
              />
              <Text
                position="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                {...Primary12Regular}
                color={GreyGrey90}
                fontWeight="600"
              >
                {unit}
                {item.value.toLocaleString()}
              </Text>
            </Box>
          </Flex>
        ))}
      </Box>
    </Box>
  )
}

export const KPIDashboard: React.FC<KPIDashboardComponentData> = ({
  title,
  metrics
}) => {
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="800px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={4}>
        {title}
      </Text>
      <Flex wrap="wrap" gap={4}>
        {metrics.map((metric, index) => (
          <Box
            key={index}
            bg="#f8f9fa"
            borderRadius="6px"
            p={3}
            minWidth="200px"
            flex="1"
          >
            <Text {...Primary12Regular} color={GreyGrey60} mb={1}>
              {metric.label}
            </Text>
            <Text
              {...Primary13SemiBold}
              fontSize="20px"
              color={GreyGrey90}
              mb={1}
            >
              {metric.value}
            </Text>
            {metric.subtitle && (
              <Text {...Primary12Regular} color={GreyGrey60} mb={2}>
                {metric.subtitle}
              </Text>
            )}
            {metric.trend && (
              <Flex align="center" gap={1}>
                <Text
                  {...Primary12Regular}
                  color={
                    metric.trend.direction === 'up'
                      ? '#10b981'
                      : metric.trend.direction === 'down'
                        ? '#ef4444'
                        : GreyGrey60
                  }
                  fontWeight="600"
                >
                  {metric.trend.direction === 'up'
                    ? '↗'
                    : metric.trend.direction === 'down'
                      ? '↘'
                      : '→'}{' '}
                  {metric.trend.percentage}
                </Text>
              </Flex>
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  )
}

export const ComparisonCard: React.FC<ComparisonComponentData> = ({
  title,
  current,
  previous,
  change
}) => {
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="500px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={4}>
        {title}
      </Text>
      <Flex justify="space-between" align="center" mb={4}>
        <Box textAlign="center">
          <Text {...Primary12Regular} color={GreyGrey60} mb={1}>
            {current.label}
          </Text>
          <Text {...Primary13SemiBold} fontSize="24px" color={GreyGrey90}>
            {current.value}
          </Text>
        </Box>
        <Box textAlign="center">
          <Text {...Primary12Regular} color={GreyGrey60} mb={1}>
            {previous.label}
          </Text>
          <Text {...Primary13SemiBold} fontSize="24px" color={GreyGrey60}>
            {previous.value}
          </Text>
        </Box>
      </Flex>
      <Box
        bg={
          change.direction === 'up'
            ? '#f0f9f4'
            : change.direction === 'down'
              ? '#fef2f2'
              : '#f8f9fa'
        }
        border={`1px solid ${change.direction === 'up' ? '#10b981' : change.direction === 'down' ? '#ef4444' : GreyGrey30}`}
        borderRadius="6px"
        p={3}
        textAlign="center"
      >
        <Text
          {...Primary13SemiBold}
          color={
            change.direction === 'up'
              ? '#10b981'
              : change.direction === 'down'
                ? '#ef4444'
                : GreyGrey60
          }
        >
          {change.direction === 'up'
            ? '↗'
            : change.direction === 'down'
              ? '↘'
              : '→'}{' '}
          {change.percentage}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60} mt={1}>
          {change.value}
        </Text>
      </Box>
    </Box>
  )
}

export const ProgressBar: React.FC<ProgressComponentData> = ({
  title,
  current,
  target,
  unit,
  subtitle
}) => {
  const percentage = Math.min((current / target) * 100, 100)

  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={4}
      mb={3}
      width="100%"
      maxWidth="600px"
    >
      <Text {...Primary13SemiBold} color={GreyGrey90} mb={2}>
        {title}
      </Text>
      {subtitle && (
        <Text {...Primary12Regular} color={GreyGrey60} mb={3}>
          {subtitle}
        </Text>
      )}
      <Flex justify="space-between" align="center" mb={2}>
        <Text {...Primary13Regular} color={GreyGrey90}>
          {unit}
          {current.toLocaleString()} / {unit}
          {target.toLocaleString()}
        </Text>
        <Text
          {...Primary13SemiBold}
          color={percentage >= 100 ? '#10b981' : GreyGrey90}
        >
          {percentage.toFixed(1)}%
        </Text>
      </Flex>
      <Box height="20px" bg="#f3f4f6" borderRadius="10px" overflow="hidden">
        <Box
          height="100%"
          bg={
            percentage >= 100
              ? '#10b981'
              : percentage >= 75
                ? IndigoIndigo50
                : percentage >= 50
                  ? YellowYellow50
                  : RedRed50
          }
          width={`${percentage}%`}
          transition="width 0.5s ease"
          borderRadius="10px"
        />
      </Box>
    </Box>
  )
}

export const InsightCard: React.FC<InsightComponentData> = ({
  title,
  message,
  severity,
  icon
}) => {
  const getColors = () => {
    switch (severity) {
      case 'success':
        return { bg: GreyGrey10, border: GreenGreen50, text: GreyGrey90 }
      case 'warning':
        return { bg: GreyGrey10, border: YellowYellow50, text: GreyGrey90 }
      case 'error':
        return { bg: GreyGrey10, border: RedRed50, text: GreyGrey90 }
      default:
        return { bg: GreyGrey10, border: IndigoIndigo50, text: GreyGrey90 }
    }
  }

  const colors = getColors()

  return (
    <Box
      bg={colors.bg}
      borderRadius="8px"
      border={`1px solid ${colors.border}`}
      p={4}
      mb={3}
      width="100%"
      maxWidth="700px"
    >
      <Flex align="flex-start" gap={3}>
        {icon && (
          <Text fontSize="20px" mt={1}>
            {icon}
          </Text>
        )}
        <Box flex={1}>
          <Text {...Primary13SemiBold} color={colors.text} mb={2}>
            {title}
          </Text>
          <Text {...Primary13Regular} color={colors.text} lineHeight="20px">
            {message}
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

export const GenerativeComponent: React.FC<{
  data: GenerativeComponentData
}> = ({ data }) => {
  switch (data.type) {
    case 'chart':
      return <SimpleChart {...data} />
    case 'pie':
      return <PieChart {...data} />
    case 'horizontal-bar':
      return <HorizontalBarChart {...data} />
    case 'table':
      return <SimpleTable {...data} />
    case 'metric':
      return <MetricCard {...data} />
    case 'kpi-dashboard':
      return <KPIDashboard {...data} />
    case 'comparison':
      return <ComparisonCard {...data} />
    case 'progress':
      return <ProgressBar {...data} />
    case 'insight':
      return <InsightCard {...data} />
    case 'invoice-card':
      return <InvoiceCard {...data} />
    case 'schedule-card':
      return <ScheduleCard {...data} />
    case 'product-card':
      return <ProductCard {...data} />
    case 'customer-pill':
      return <CustomerPill {...data} />
    default:
      return null
  }
}

export const InvoiceCard: React.FC<InvoiceCardComponentData> = ({
  invoiceId,
  invoiceNumber,
  amount,
  status,
  url
}) => {
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={3}
      mb={2}
      width="100%"
      maxWidth="560px"
    >
      <Flex justify="space-between" align="center" gap={2}>
        <Box>
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            <Box
              as={Link.Unstyled}
              to={url || `/invoices/${invoiceId}`}
              target="_blank"
              rel="noopener noreferrer"
              color={String(IndigoIndigo50)}
            >
              {invoiceNumber}
            </Box>
          </Text>
        </Box>
        <Box textAlign="right">
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            {amount}
          </Text>
          {status && (
            <Text {...Primary12Regular} color={GreyGrey60}>
              {status}
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  )
}

export const ScheduleCard: React.FC<ScheduleCardComponentData> = ({
  scheduleId,
  customerId,
  status,
  mrr
}) => {
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={3}
      mb={2}
      width="100%"
      maxWidth="560px"
    >
      <Flex justify="space-between" align="center" gap={2}>
        <Box>
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            <Box
              as={Link.Unstyled}
              to={`/billing/schedules/${scheduleId}`}
              target="_blank"
              rel="noopener noreferrer"
              color={String(IndigoIndigo50)}
            >
              Schedule
            </Box>
          </Text>
        </Box>
        <Box textAlign="right">
          {mrr && (
            <Text {...Primary13SemiBold} color={GreyGrey90}>
              MRR {mrr}
            </Text>
          )}
          {status && (
            <Text {...Primary12Regular} color={GreyGrey60}>
              {status}
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  )
}

export type ProductCardComponentData = {
  type: 'product-card'
  title: string
  productId: string
  name: string
  recognitionMethod: string
}

export const ProductCard: React.FC<ProductCardComponentData> = ({
  productId,
  name,
  recognitionMethod
}) => {
  const prettyMethod = recognitionMethod
    ? humanize(String(recognitionMethod).toLowerCase())
    : '—'
  return (
    <Box
      bg="white"
      borderRadius="8px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={3}
      mb={2}
      width="100%"
      maxWidth="560px"
    >
      <Flex justify="space-between" align="center" gap={2}>
        <Box>
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            <Box
              as={Link.Unstyled}
              to={`/products/${productId}`}
              target="_blank"
              rel="noopener noreferrer"
              color={String(IndigoIndigo50)}
            >
              {name}
            </Box>
          </Text>
        </Box>
        <Box textAlign="right">
          <Text {...Primary12Regular} color={GreyGrey60}>
            Revenue method
          </Text>
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            {prettyMethod}
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

export type CustomerPillComponentData = {
  type: 'customer-pill'
  customerId?: string
  name: string
}

export const CustomerPill: React.FC<CustomerPillComponentData> = ({
  customerId,
  name
}) => {
  const initials = String(name || '?')
    .split(' ')
    .map(s => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={2}
      bg="white"
      borderRadius="9999px"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      p={2}
      px={3}
      mb={2}
      width="auto"
      maxWidth="560px"
    >
      <Flex
        align="center"
        justify="center"
        width="20px"
        height="20px"
        borderRadius="9999px"
        bg={IndigoIndigo50}
        color="white"
        fontSize="10px"
        fontWeight={700}
      >
        {initials}
      </Flex>
      <Text {...Primary13Regular} color={GreyGrey90}>
        {name}
        {customerId ? ` · ${customerId.slice(0, 8)}` : ''}
      </Text>
    </Box>
  )
}
